/**
 * Asset register + scoping — V102-3-2, V102-3-3 / ADR-0057.
 *
 * `assets` are architecture components (identity, database, web front end,
 * middle tier, AI service, endpoint fleet, network, other), optionally under
 * an AI system and optionally provider-hosted. A program that declares
 * `scoping` in its manifest (CMMC: the five 32 CFR 170.19 categories)
 * categorizes each asset per period in `asset_period_memberships`; programs
 * without `scoping` keep system-level boundaries, and categorizing an asset
 * for them is refused.
 *
 * Discovery suggests, never adds (§4): `suggestAssets` derives candidates
 * from what is already collected and returns each with its source; accepting
 * one is a separate `createAsset` call, and categorizing it is another.
 *
 * Every function takes `workspaceId` and scopes every query by it; another
 * workspace's rows are `not_found`.
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { sqlIn } from '../utils/sql-in.js';
import { excludedCiAssets } from './configuration-item-grace-service.js';
import { getProgramManifest } from '../programs/manifests.js';
import { getConnectorManifest } from '../connectors/manifests.js';
import { getProvider } from '../connectors/providers.js';
import { dispositionContext, dispositionsFor } from './infra-resource-disposition.js';
import { countedEnvironments } from './customer-environment-service.js';
export const ASSET_TIERS = [
    'identity', 'database', 'web_front_end', 'middle_tier', 'ai_service', 'endpoint_fleet', 'network', 'other',
];
export class AssetError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'AssetError';
    }
}
const fail = (code, m) => {
    throw new AssetError(code, m);
};
const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
function isTier(v) {
    return typeof v === 'string' && ASSET_TIERS.includes(v);
}
async function assertSystem(db, workspaceId, systemId) {
    const r = await db.query(`SELECT 1 AS ok FROM ai_systems WHERE system_id = ? AND workspace_id = ? LIMIT 1`, [systemId, workspaceId]);
    if (!r.results.length)
        fail('not_found', 'System not found.');
}
async function externalService(db, workspaceId, id) {
    const r = await db.query(`SELECT provider_key FROM external_services WHERE external_service_id = ? AND workspace_id = ? LIMIT 1`, [id, workspaceId]);
    return r.results[0] ?? fail('not_found', 'External service not found.');
}
export async function createAsset(db, input) {
    const name = text(input.name) ?? fail('invalid', 'name is required');
    if (name.length > 200)
        fail('invalid', 'name is at most 200 characters');
    if (!isTier(input.tier))
        fail('invalid', `tier must be one of ${ASSET_TIERS.join(', ')}`);
    const createdBy = text(input.createdBy) ?? fail('invalid', 'createdBy is required');
    const systemId = text(input.systemId);
    if (systemId)
        await assertSystem(db, input.workspaceId, systemId);
    const extId = text(input.externalServiceId);
    let providerKey = text(input.providerKey);
    if (extId) {
        const svc = await externalService(db, input.workspaceId, extId);
        if (providerKey && providerKey !== svc.provider_key)
            fail('invalid', 'providerKey must match the external service.');
        providerKey = svc.provider_key;
    }
    const discoveredFrom = text(input.discoveredFrom) ?? 'manual';
    if (!/^[a-z0-9_:.-]{1,80}$/.test(discoveredFrom))
        fail('invalid', 'discoveredFrom must be a connector kind or source key');
    const id = input.assetId ?? generateRandomId('ast');
    const at = (input.now ?? new Date()).toISOString();
    await db.execute(`INSERT INTO assets (asset_id, workspace_id, system_id, name, tier, external_service_id, provider_key, discovered_from, created_by, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, input.workspaceId, systemId, name, input.tier, extId, providerKey, discoveredFrom, createdBy, at, at]);
    return (await getAsset(db, input.workspaceId, id));
}
export async function getAsset(db, workspaceId, assetId) {
    const r = await db.query(`SELECT * FROM assets WHERE asset_id = ? AND workspace_id = ? LIMIT 1`, [assetId, workspaceId]);
    return r.results[0] ?? null;
}
export async function listAssets(db, workspaceId, opts = {}) {
    const where = ['workspace_id = ?'];
    const params = [workspaceId];
    if (!opts.includeRetired)
        where.push('retired_at IS NULL');
    if (opts.systemId) {
        where.push('system_id = ?');
        params.push(opts.systemId);
    }
    if (opts.tier) {
        where.push('tier = ?');
        params.push(opts.tier);
    }
    const r = await db.query(`SELECT * FROM assets WHERE ${where.join(' AND ')} ORDER BY name, asset_id`, params);
    return r.results;
}
export async function updateAsset(db, input) {
    const cur = await getAsset(db, input.workspaceId, input.assetId);
    if (!cur)
        fail('not_found', 'Asset not found.');
    if (cur.retired_at)
        fail('conflict', 'A retired asset cannot be changed.');
    const name = input.name === undefined ? cur.name : (text(input.name) ?? fail('invalid', 'name is required'));
    const tier = input.tier === undefined ? cur.tier : input.tier;
    if (!isTier(tier))
        fail('invalid', `tier must be one of ${ASSET_TIERS.join(', ')}`);
    const systemId = input.systemId === undefined ? cur.system_id : text(input.systemId);
    // ADR-0100 §1: a CI-linked asset IS the (system, CI) link; its system is its identity.
    if (cur.component_id && systemId !== cur.system_id)
        fail('conflict', 'This asset stands for a configuration item in its system; link the item to another system instead.');
    if (systemId && systemId !== cur.system_id)
        await assertSystem(db, input.workspaceId, systemId);
    await db.execute(`UPDATE assets SET name = ?, tier = ?, system_id = ?, updated_at = ? WHERE asset_id = ? AND workspace_id = ? AND retired_at IS NULL`, [name, tier, systemId, (input.now ?? new Date()).toISOString(), input.assetId, input.workspaceId]);
    return (await getAsset(db, input.workspaceId, input.assetId));
}
/**
 * Retire an asset. Refused while it is in an assessed category of a period
 * in fieldwork: take it out of that period's scope first (recorded).
 */
export async function retireAsset(db, input) {
    const cur = await getAsset(db, input.workspaceId, input.assetId);
    if (!cur)
        fail('not_found', 'Asset not found.');
    if (cur.retired_at)
        return cur;
    const by = text(input.retiredBy) ?? fail('invalid', 'retiredBy is required');
    const live = await db.query(`SELECT m.period_id, m.category, c.program
       FROM asset_period_memberships m
       JOIN audit_periods p ON p.period_id = m.period_id
       JOIN certification_profiles c ON c.profile_id = m.profile_id
      WHERE m.workspace_id = ? AND m.asset_id = ? AND m.removed_at IS NULL AND p.status = 'fieldwork'`, [input.workspaceId, input.assetId]);
    const blocking = live.results.find((r) => categoryTreatment(r.program, r.category) === 'assessed');
    if (blocking)
        fail('conflict', `The asset is in scope for period ${blocking.period_id}, which is in fieldwork; remove it from that period first.`);
    const at = (input.now ?? new Date()).toISOString();
    await db.execute(`UPDATE assets SET retired_at = ?, retired_by = ?, updated_at = ? WHERE asset_id = ? AND workspace_id = ? AND retired_at IS NULL`, [at, by, at, input.assetId, input.workspaceId]);
    return (await getAsset(db, input.workspaceId, input.assetId));
}
// ---- scoping (per program × period) --------------------------------------------------
/** The manifest's scoping categories for a program; null when it scopes systems, not assets. */
export function scopingCategories(programKey) {
    return getProgramManifest(programKey)?.scoping?.categories ?? null;
}
/** The treatment a program gives a category; null for an unknown program or key. */
export function categoryTreatment(programKey, category) {
    return scopingCategories(programKey)?.find((c) => c.key === category)?.treatment ?? null;
}
async function periodWithProgram(db, workspaceId, periodId) {
    const r = await db.query(`SELECT p.period_id, p.profile_id, p.status, c.program
       FROM audit_periods p JOIN certification_profiles c ON c.profile_id = p.profile_id AND c.workspace_id = p.workspace_id
      WHERE p.period_id = ? AND p.workspace_id = ? LIMIT 1`, [periodId, workspaceId]);
    return r.results[0] ?? fail('not_found', 'Audit period not found.');
}
/**
 * Set (or change) an asset's category for one period. Refused when the
 * program declares no `scoping`, or the key is not one of its categories.
 * Non-assessed categories (documented / excluded) need a rationale. A change
 * removes the previous membership (kept as history) and adds a new one.
 */
export async function setAssetCategory(db, input) {
    const period = await periodWithProgram(db, input.workspaceId, input.periodId);
    if (period.status === 'closed')
        fail('conflict', 'This period is closed; its scope is final.');
    const cats = scopingCategories(period.program);
    if (!cats)
        fail('invalid', `${getProgramManifest(period.program)?.label ?? period.program} scopes systems, not assets; asset categories do not apply.`);
    const cat = cats.find((c) => c.key === input.category);
    if (!cat)
        fail('invalid', `Unknown category ${input.category}; use one of ${cats.map((c) => c.key).join(', ')}.`);
    const rationale = text(input.rationale);
    if (cat.treatment !== 'assessed' && !rationale)
        fail('invalid', `${cat.label} needs a rationale.`);
    const decidedBy = text(input.decidedBy) ?? fail('invalid', 'decidedBy is required');
    const asset = await getAsset(db, input.workspaceId, input.assetId);
    if (!asset)
        fail('not_found', 'Asset not found.');
    if (asset.retired_at)
        fail('conflict', 'A retired asset cannot be scoped.');
    const cur = await activeMembership(db, input.workspaceId, input.periodId, input.assetId);
    if (cur && cur.category === cat.key && (cur.rationale ?? null) === rationale)
        return cur;
    const at = (input.now ?? new Date()).toISOString();
    const id = generateRandomId('apm');
    const statements = [];
    if (cur) {
        statements.push({
            sql: `UPDATE asset_period_memberships SET removed_at = ?, removed_by = ?, removed_reason = ?
             WHERE membership_id = ? AND workspace_id = ? AND removed_at IS NULL`,
            params: [at, decidedBy, `recategorized as ${cat.key}`, cur.membership_id, input.workspaceId],
        });
    }
    statements.push({
        sql: `INSERT INTO asset_period_memberships (membership_id, workspace_id, profile_id, period_id, asset_id, category, rationale, decided_by, decided_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [id, input.workspaceId, period.profile_id, input.periodId, input.assetId, cat.key, rationale, decidedBy, at],
    });
    await db.batch(statements);
    return (await activeMembership(db, input.workspaceId, input.periodId, input.assetId));
}
/** Take an asset out of a period's scope (recorded with a reason). */
export async function removeAssetFromPeriod(db, input) {
    const period = await periodWithProgram(db, input.workspaceId, input.periodId);
    if (period.status === 'closed')
        fail('conflict', 'This period is closed; its scope is final.');
    const by = text(input.removedBy) ?? fail('invalid', 'removedBy is required');
    const reason = text(input.reason) ?? fail('invalid', 'A reason is required.');
    const cur = await activeMembership(db, input.workspaceId, input.periodId, input.assetId);
    if (!cur)
        fail('not_found', 'The asset is not scoped in this period.');
    await db.execute(`UPDATE asset_period_memberships SET removed_at = ?, removed_by = ?, removed_reason = ?
      WHERE membership_id = ? AND workspace_id = ? AND removed_at IS NULL`, [(input.now ?? new Date()).toISOString(), by, reason, cur.membership_id, input.workspaceId]);
}
async function activeMembership(db, workspaceId, periodId, assetId) {
    const r = await db.query(`SELECT * FROM asset_period_memberships WHERE workspace_id = ? AND period_id = ? AND asset_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, periodId, assetId]);
    return r.results[0] ?? null;
}
/** ADR-0091 §2: the synthesized category of a resource-scoped asset (never stored). */
export const RESOURCE_SCOPE_CATEGORY = 'in_boundary_resource';
export const RESOURCE_SCOPE_ACTOR = 'system:resource-scope';
/** ISO or `sqlNow()` text (UTC) → epoch ms; NaN when unreadable. */
function scopeTimeMs(s) {
    if (!s)
        return Number.NaN;
    let v = s.trim().replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(v))
        v += 'T00:00:00Z';
    if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(v))
        v += 'Z';
    return Date.parse(v);
}
/**
 * The resource scope as of `asOf` (V109-6 security M1: an export is scoped as of its sweep's end,
 * not the export time). A resource is part of the scope at `asOf` when it was first seen by then
 * and was not gone by then; it is linked when its asset link was made by then (a link made later
 * reads as unlinked at `asOf`). Boundary dispositions are the recorded ones (they carry no history).
 */
async function resourceScope(db, workspaceId, period, asOf) {
    const rows = (await db.query(`SELECT r.resource_id, r.item_id, r.system_id, r.asset_id, r.environment, r.asset_linked_at, r.first_seen_at, r.gone_at, i.status AS i_status, i.external_service_id AS i_service
       FROM infra_resources r JOIN infra_inventory_items i ON i.item_id = r.item_id AND i.workspace_id = r.workspace_id
      WHERE r.workspace_id = ?
      ORDER BY r.resource_id`, [workspaceId])).results;
    const asOfMs = asOf.getTime();
    const out = [];
    const unlinked = [];
    if (!rows.length)
        return { assets: out, unlinked, as_of: asOf.toISOString() };
    const ctx = await dispositionContext(db, workspaceId);
    const counted = await countedEnvironments(db, { workspaceId, profileId: period.profile_id });
    const seen = new Set();
    const assets = new Map();
    for (const r of rows) {
        const firstSeen = scopeTimeMs(r.first_seen_at);
        if (Number.isFinite(firstSeen) && firstSeen > asOfMs)
            continue; // not there yet
        if (r.gone_at) {
            const gone = scopeTimeMs(r.gone_at);
            if (!Number.isFinite(gone) || gone <= asOfMs)
                continue; // gone by then (an unreadable gone_at reads as gone)
        }
        const env = r.environment ?? 'production';
        if (!counted.has(env))
            continue;
        const linkedMs = scopeTimeMs(r.asset_linked_at);
        const linkedAsset = r.asset_id && (!r.asset_linked_at || !Number.isFinite(linkedMs) || linkedMs <= asOfMs) ? r.asset_id : null;
        const d = dispositionsFor(ctx, { resource_id: r.resource_id, item_id: r.item_id, asset_id: linkedAsset }, { item_id: r.item_id, status: r.i_status, external_service_id: r.i_service })
            .by_program.find((p) => p.profile_id === period.profile_id)?.disposition ?? 'unresolved';
        if (d !== 'in_boundary' && d !== 'unresolved')
            continue;
        let asset = null;
        if (linkedAsset) {
            if (!assets.has(linkedAsset))
                assets.set(linkedAsset, await getAsset(db, workspaceId, linkedAsset));
            const a = assets.get(linkedAsset) ?? null;
            asset = a && !a.retired_at ? a : null;
        }
        // Unresolved resources are never in scope; they (and in-boundary resources with no live link) are coverage gaps.
        if (!asset || d !== 'in_boundary') {
            unlinked.push({ resource_id: r.resource_id, item_id: r.item_id, system_id: r.system_id, disposition: d, environment: env });
            continue;
        }
        if (seen.has(asset.asset_id))
            continue;
        seen.add(asset.asset_id);
        out.push({
            asset,
            membership: {
                membership_id: `resource-scope:${asset.asset_id}`, workspace_id: workspaceId, profile_id: period.profile_id, period_id: period.period_id,
                asset_id: asset.asset_id, category: RESOURCE_SCOPE_CATEGORY, rationale: 'In the program boundary (linked resource, counted environment).',
                decided_by: RESOURCE_SCOPE_ACTOR, decided_at: r.asset_linked_at ?? r.first_seen_at, removed_at: null, removed_by: null, removed_reason: null,
            },
            treatment: 'assessed',
            category_label: 'In boundary',
        });
    }
    out.sort((a, b) => (a.asset.name < b.asset.name ? -1 : a.asset.name > b.asset.name ? 1 : a.asset.asset_id < b.asset.asset_id ? -1 : 1));
    return { assets: out, unlinked, as_of: asOf.toISOString() };
}
/** Does this period's program take its asset scope from in-boundary resources (ADR-0091 §2)? */
function usesResourceScope(program) {
    return !scopingCategories(program) && getProgramManifest(program)?.resourceScope === 'in_boundary_resources';
}
/** ADR-0100 §1 (O-1): the synthesized category of a CI-in-system asset in a system-scoped program (never stored). */
export const LINKED_CI_SCOPE_CATEGORY = 'linked_configuration_item';
export const LINKED_CI_SCOPE_ACTOR = 'system:linked-ci-scope';
/** Does this period's program assess the CIs its in-scope systems link (ADR-0100 §1, manifest flag)? */
export function usesLinkedCiScope(program) {
    return !scopingCategories(program) && getProgramManifest(program)?.resourceScope === 'linked_configuration_items';
}
/**
 * The systems in a program's period boundary: the period's own `boundary_memberships`, else the
 * program's standing boundary, else (v9.6 compatibility) the profile's `system_ids_json` — the same
 * order `getBoundary` reads. Only systems of this workspace.
 */
export async function periodBoundarySystemIds(db, workspaceId, profileId, periodId) {
    const rows = async (pid) => (await db.query(`SELECT DISTINCT b.system_id FROM boundary_memberships b JOIN ai_systems s ON s.system_id = b.system_id
       LEFT JOIN customer_profiles cp ON cp.profile_id = s.profile_id
      WHERE b.workspace_id = ? AND b.profile_id = ? AND ${pid ? 'b.period_id = ?' : 'b.period_id IS NULL'} AND b.removed_at IS NULL
        AND COALESCE(s.workspace_id, cp.workspace_id) = b.workspace_id
      ORDER BY b.system_id`, pid ? [workspaceId, profileId, pid] : [workspaceId, profileId])).results.map((r) => r.system_id);
    let ids = periodId ? await rows(periodId) : [];
    if (!ids.length)
        ids = await rows(null);
    if (ids.length)
        return ids;
    const p = (await db.query(`SELECT system_ids_json FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? LIMIT 1`, [profileId, workspaceId])).results[0];
    let scoped = [];
    try {
        scoped = JSON.parse(p?.system_ids_json ?? '[]').filter((x) => typeof x === 'string');
    }
    catch {
        scoped = [];
    }
    if (!scoped.length)
        return [];
    const inList = sqlIn(scoped);
    return (await db.query(`SELECT s.system_id FROM ai_systems s LEFT JOIN customer_profiles cp ON cp.profile_id = s.profile_id
      WHERE s.system_id IN ${inList.sql} AND COALESCE(s.workspace_id, cp.workspace_id) = ? ORDER BY s.system_id`, [...inList.params, workspaceId])).results.map((r) => r.system_id);
}
/**
 * ADR-0100 §1 (O-1): for a system-scoped program (`resourceScope: 'linked_configuration_items'`), the
 * in-scope assets of a per-asset objective are the live CI-linked asset rows of the systems in the
 * period's boundary (one per CI-in-system; a retired / merged CI or a retired asset is out), synthesized
 * the ADR-0091 §2 way: treatment `assessed`, never stored. Discovery creates the rows; only this rule puts
 * them in scope.
 */
async function linkedCiScope(db, workspaceId, period, asOf, content) {
    return (await linkedCiScopeWithReview(db, workspaceId, period, asOf, content)).assets;
}
/**
 * D-113-28: the linked-CI scope without the assets still `pending review` (grace window) or marked out of scope,
 * plus the pending ones so readers can report them (never silently zero).
 */
async function linkedCiScopeWithReview(db, workspaceId, period, asOf, content) {
    const all = await linkedCiScopeAll(db, workspaceId, period);
    if (!all.length)
        return { assets: [], pending: [], out_of_scope: [] };
    const { excluded, pending, out_of_scope } = await excludedCiAssets(db, workspaceId, { now: asOf, content, systemIds: [...new Set(all.map((s) => s.asset.system_id))] });
    return { assets: all.filter((s) => !excluded.has(s.asset.asset_id)), pending, out_of_scope };
}
/** D-113-28: the CI assets of a system-scoped period that are pending review (empty for other programs). */
export async function periodPendingReview(db, workspaceId, periodId, asOf, content) {
    return (await periodCiReview(db, workspaceId, periodId, asOf, content)).pending;
}
/** D-113-28 + review H2: the period's CI assets that are not cells — pending review and out of scope (with labels). */
export async function periodCiReview(db, workspaceId, periodId, asOf, content) {
    const period = await periodWithProgram(db, workspaceId, periodId);
    if (!usesLinkedCiScope(period.program))
        return { pending: [], out_of_scope: [] };
    const r = await linkedCiScopeWithReview(db, workspaceId, period, asOf, content);
    return { pending: r.pending, out_of_scope: r.out_of_scope };
}
async function linkedCiScopeAll(db, workspaceId, period) {
    const systems = await periodBoundarySystemIds(db, workspaceId, period.profile_id, period.period_id);
    if (!systems.length)
        return [];
    const inList = sqlIn(systems);
    const rows = (await db.query(`SELECT a.* FROM assets a JOIN ai_components c ON c.component_id = a.component_id AND c.workspace_id = a.workspace_id
      WHERE a.workspace_id = ? AND a.system_id IN ${inList.sql} AND a.retired_at IS NULL AND c.retired_at IS NULL AND c.merged_into_id IS NULL
      ORDER BY a.name, a.asset_id`, [workspaceId, ...inList.params])).results;
    return rows.map((asset) => ({
        asset,
        membership: {
            membership_id: `linked-ci-scope:${asset.asset_id}`, workspace_id: workspaceId, profile_id: period.profile_id, period_id: period.period_id,
            asset_id: asset.asset_id, category: LINKED_CI_SCOPE_CATEGORY, rationale: 'A configuration item of an in-scope system.',
            decided_by: LINKED_CI_SCOPE_ACTOR, decided_at: asset.created_at, removed_at: null, removed_by: null, removed_reason: null,
        },
        treatment: 'assessed',
        category_label: 'Configuration item',
    }));
}
/**
 * The period's resource scope as of `asOf` (default now), or null when the program scopes assets by
 * category memberships instead (V109-6 security M1).
 */
export async function periodResourceScope(db, workspaceId, periodId, asOf) {
    const period = await periodWithProgram(db, workspaceId, periodId);
    if (!usesResourceScope(period.program))
        return null;
    return resourceScope(db, workspaceId, period, asOf ?? new Date());
}
/** Every live membership in the period with its treatment (all categories). `asOf` applies to resource-scoped programs. */
export async function listPeriodAssetScope(db, workspaceId, periodId, asOf, content) {
    const period = await periodWithProgram(db, workspaceId, periodId);
    // ADR-0091 §2: the default resource scope for programs without scoping categories.
    if (usesResourceScope(period.program)) {
        return (await resourceScope(db, workspaceId, period, asOf ?? new Date())).assets;
    }
    // ADR-0100 §1 (O-1): the CI-linked assets of the period boundary's systems.
    if (usesLinkedCiScope(period.program))
        return linkedCiScope(db, workspaceId, period, asOf, content);
    const cats = scopingCategories(period.program) ?? [];
    const r = await db.query(`SELECT m.*, a.name AS a_name FROM asset_period_memberships m JOIN assets a ON a.asset_id = m.asset_id AND a.workspace_id = m.workspace_id
      WHERE m.workspace_id = ? AND m.period_id = ? AND m.removed_at IS NULL AND a.retired_at IS NULL
      ORDER BY a.name, m.asset_id`, [workspaceId, periodId]);
    const out = [];
    for (const row of r.results) {
        const cat = cats.find((c) => c.key === row.category);
        if (!cat)
            continue; // a key the manifest no longer declares is not scoped
        const { a_name: _name, ...membership } = row;
        const asset = (await getAsset(db, workspaceId, row.asset_id));
        out.push({ asset, membership, treatment: cat.treatment, category_label: cat.label });
    }
    return out;
}
/** Assets judged by per-asset objectives this period: live memberships whose category is `assessed`. */
export async function listInScopeAssets(db, workspaceId, periodId, asOf, content) {
    return (await listPeriodAssetScope(db, workspaceId, periodId, asOf, content)).filter((s) => s.treatment === 'assessed');
}
const FAMILY_TIER = {
    idp: 'identity',
    cloud: 'network',
    ai_provider: 'ai_service',
    ai_gateway: 'ai_service',
    ml_registry: 'ai_service',
    vector_db: 'database',
    kubernetes: 'middle_tier',
    saas: 'other',
};
const SERVICE_KIND_TIER = {
    model_api: 'ai_service',
    vector_db: 'database',
    hosting: 'middle_tier',
    observability: 'other',
    data_processing: 'other',
    other: 'other',
};
const norm = (s) => s.trim().toLowerCase();
/**
 * Candidate assets derived from what the workspace already collects. Never
 * writes. Excludes candidates that match a live asset (same external
 * service, same provider + system, or same name + system).
 */
export async function suggestAssets(db, workspaceId) {
    const existing = await listAssets(db, workspaceId);
    const taken = new Set();
    for (const a of existing) {
        if (a.external_service_id)
            taken.add(`ext:${a.external_service_id}`);
        if (a.provider_key)
            taken.add(`prov:${a.provider_key}:${a.system_id ?? ''}`);
        taken.add(`name:${norm(a.name)}:${a.system_id ?? ''}`);
    }
    const out = [];
    const add = (s) => {
        const keys = [
            s.external_service_id ? `ext:${s.external_service_id}` : null,
            s.provider_key ? `prov:${s.provider_key}:${s.system_id ?? ''}` : null,
            `name:${norm(s.name)}:${s.system_id ?? ''}`,
        ].filter((k) => !!k);
        if (keys.some((k) => taken.has(k)))
            return;
        for (const k of keys)
            taken.add(k);
        out.push(s);
    };
    // External services the workspace declared (provider-hosted assets).
    const services = await db.query(`SELECT external_service_id, name, provider_key, service_kind FROM external_services
      WHERE workspace_id = ? AND retired_at IS NULL ORDER BY name, external_service_id`, [workspaceId]);
    for (const s of services.results) {
        add({
            suggestion_key: `external_service:${s.external_service_id}`, name: s.name, tier: SERVICE_KIND_TIER[s.service_kind] ?? 'other',
            system_id: null, external_service_id: s.external_service_id, provider_key: s.provider_key, discovered_from: 'external_service',
            source: { kind: 'external_service', id: s.external_service_id, label: s.name },
        });
    }
    // Active connectors: the IdP, cloud accounts, AI providers, vector stores, clusters.
    const connectors = await db.query(`SELECT connector_id, kind, provider_key FROM evidence_connectors WHERE workspace_id = ? AND status = 'active' ORDER BY kind, connector_id`, [workspaceId]);
    for (const c of connectors.results) {
        const m = getConnectorManifest(c.kind);
        // G-1k: a manifest's asset tier overrides its family default.
        const tier = m ? (m.asset?.tier ?? FAMILY_TIER[m.family]) : undefined;
        if (!m || !tier)
            continue; // scm / vuln / observability / ingest observe assets; they are not assets
        const providerKey = c.provider_key ?? m.provider.key ?? null;
        const label = m.ui.label;
        add({
            suggestion_key: `connector:${c.connector_id}`, name: tier === 'identity' ? `${label} (identity provider)` : label, tier,
            system_id: null, external_service_id: null, provider_key: providerKey, discovered_from: c.kind,
            source: { kind: 'connector', id: c.connector_id, label },
        });
    }
    // AI systems in the inventory (one AI service asset each).
    const systems = await db.query(`SELECT system_id, name FROM ai_systems WHERE workspace_id = ? AND deployment_status <> 'retired' ORDER BY name, system_id`, [workspaceId]);
    for (const s of systems.results) {
        add({
            suggestion_key: `ai_system:${s.system_id}`, name: s.name, tier: 'ai_service',
            system_id: s.system_id, external_service_id: null, provider_key: null, discovered_from: 'ai_system',
            source: { kind: 'ai_system', id: s.system_id, label: s.name },
        });
    }
    // Sanctioned AI tools discovered from IdP grants / telemetry.
    const tools = await db.query(`SELECT tool_id, name, provider_key FROM discovered_tools WHERE workspace_id = ? AND status = 'sanctioned' ORDER BY name, tool_id`, [workspaceId]);
    for (const t of tools.results) {
        const provider = t.provider_key ? getProvider(t.provider_key) : undefined;
        add({
            suggestion_key: `discovered_tool:${t.tool_id}`, name: provider?.name ?? t.name, tier: 'ai_service',
            system_id: null, external_service_id: null, provider_key: t.provider_key, discovered_from: 'discovered_tool',
            source: { kind: 'discovered_tool', id: t.tool_id, label: t.name },
        });
    }
    return out;
}
/**
 * Upsert the viewer's uncommitted {category, rationale} for one asset × period.
 * Validates the category against the program's manifest; a value the manifest
 * no longer declares is refused. Rationale is optional at the draft tier
 * (the commit step (`setAssetCategory`) enforces non-assessed-needs-rationale).
 */
export async function setAssetDraft(db, input) {
    const userId = text(input.userId) ?? fail('invalid', 'userId is required');
    const period = await periodWithProgram(db, input.workspaceId, input.periodId);
    const cats = scopingCategories(period.program);
    if (!cats)
        fail('invalid', 'This program scopes systems, not assets.');
    if (!cats.some((c) => c.key === input.category))
        fail('invalid', `Unknown category ${input.category}.`);
    const asset = await getAsset(db, input.workspaceId, input.assetId);
    if (!asset)
        fail('not_found', 'Asset not found.');
    if (asset.retired_at)
        fail('conflict', 'A retired asset cannot be drafted.');
    const rationale = text(input.rationale ?? null);
    const at = (input.now ?? new Date()).toISOString();
    const existing = await db.query(`SELECT * FROM asset_scope_drafts WHERE workspace_id = ? AND period_id = ? AND asset_id = ? AND user_id = ? LIMIT 1`, [input.workspaceId, input.periodId, input.assetId, userId]);
    if (existing.results[0]) {
        await db.execute(`UPDATE asset_scope_drafts SET category = ?, rationale = ?, updated_at = ?
        WHERE draft_id = ? AND workspace_id = ?`, [input.category, rationale, at, existing.results[0].draft_id, input.workspaceId]);
        return (await getAssetDraft(db, input.workspaceId, input.periodId, input.assetId, userId));
    }
    const id = generateRandomId('asd');
    await db.execute(`INSERT INTO asset_scope_drafts (draft_id, workspace_id, period_id, asset_id, user_id, category, rationale, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, input.workspaceId, input.periodId, input.assetId, userId, input.category, rationale, at, at]);
    return (await getAssetDraft(db, input.workspaceId, input.periodId, input.assetId, userId));
}
export async function getAssetDraft(db, workspaceId, periodId, assetId, userId) {
    const r = await db.query(`SELECT * FROM asset_scope_drafts WHERE workspace_id = ? AND period_id = ? AND asset_id = ? AND user_id = ? LIMIT 1`, [workspaceId, periodId, assetId, userId]);
    return r.results[0] ?? null;
}
/** Clear the viewer's draft after committing or discarding. */
export async function clearAssetDraft(db, workspaceId, periodId, assetId, userId) {
    await db.execute(`DELETE FROM asset_scope_drafts WHERE workspace_id = ? AND period_id = ? AND asset_id = ? AND user_id = ?`, [workspaceId, periodId, assetId, userId]);
}
/** Every live draft the viewer owns for a period (used to overlay the §2.1 preview). */
export async function listAssetDraftsForUser(db, workspaceId, periodId, userId) {
    const r = await db.query(`SELECT * FROM asset_scope_drafts WHERE workspace_id = ? AND period_id = ? AND user_id = ?
      ORDER BY updated_at DESC, draft_id`, [workspaceId, periodId, userId]);
    return r.results;
}
//# sourceMappingURL=asset-service.js.map